import { storage } from '../services/storage.js';
import {
  getActiveBrokerId,
  getActiveBrokerName,
  getAvailableBrokers,
  getBroker,
  hasDefaultSandboxKey,
  isBrokerMode,
  isClientKeyEntryAllowed,
  isProductionEnv
} from '../services/broker/broker-registry.js';
import { createBrokerSelector } from './broker-selector.js';

const EYE_ICON_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';

function apiKeyFieldHtml(label, placeholder, value, { disabled = false } = {}) {
  return `
      <div class="api-label" style="margin-top:6px;">${label}</div>
      <div class="api-key-input-wrap">
        <input type="password" class="api-key-input js-api-key" placeholder="${placeholder}" value="${value}"${disabled ? ' disabled' : ''}>
        <button type="button" class="api-key-toggle js-api-key-toggle" aria-label="Show/hide key"${disabled ? ' disabled' : ''}>${EYE_ICON_SVG}</button>
      </div>`;
}

function wireToggle(input, toggle) {
  toggle?.addEventListener('click', () => {
    if (!input || input.disabled) return;
    input.type = input.type === 'password' ? 'text' : 'password';
    toggle.classList.toggle('active', input.type === 'text');
  });
}

export function createApiConfig() {
  const el = document.createElement('div');
  el.className = 'api-section';
  const availableBrokers = getAvailableBrokers();
  const brokerSelector = createBrokerSelector({
    onChange() {
      render();
    }
  });
  if (availableBrokers.length > 1) {
    el.appendChild(brokerSelector.el);
  }

  const content = document.createElement('div');
  el.appendChild(content);

  function renderTradierConfig() {
    const { isSandbox } = storage.getMode();
    const brokerMode = isBrokerMode();
    const useOwnKey = storage.getUseOwnKey();
    const allowClientKeyEntry = isClientKeyEntryAllowed();
    const apiKey = storage.getApiKey();

    let html = '';

    // Sandbox / Production toggle
    html += `
      <div class="mode-row">
        <span class="mode-label ${isSandbox ? 'sandbox' : 'production'}">${isSandbox ? 'Sandbox' : 'Production'}</span>
        <label class="toggle-switch">
          <input type="checkbox" class="js-sandbox-toggle" ${isSandbox ? 'checked' : ''}>
          <span class="toggle-slider"></span>
        </label>
      </div>`;

    if (brokerMode && isSandbox && hasDefaultSandboxKey()) {
      // Sandbox + broker mode + default key available: show own-key toggle
      html += `
      <div class="mode-row" style="margin-top:6px;">
        <span class="mode-label">Use my own sandbox key</span>
        <label class="toggle-switch">
          <input type="checkbox" class="js-own-key-toggle" ${useOwnKey ? 'checked' : ''}>
          <span class="toggle-slider"></span>
        </label>
      </div>`;
      if (useOwnKey) {
        html += apiKeyFieldHtml('Tradier Sandbox API Key', 'Enter Tradier sandbox key', apiKey);
      } else {
        html += `
      <div class="api-label" style="margin-top:6px;">Using default sandbox key</div>`;
      }
    } else if (brokerMode && isSandbox) {
      // Sandbox + broker mode + no default key: user must enter their own
      html += apiKeyFieldHtml('Tradier Sandbox API Key', 'Enter Tradier sandbox key', apiKey);
    } else if (brokerMode && !isSandbox) {
      // Production + broker mode: user enters their own production key
      html += apiKeyFieldHtml('Tradier Production API Key', 'Enter Tradier production key', apiKey);
    } else {
      // Client mode: direct key entry
      html += apiKeyFieldHtml('Tradier API Key', 'Enter Tradier API key', apiKey);
    }

    content.innerHTML = html;

    const input = content.querySelector('.js-api-key');
    const sandboxToggle = content.querySelector('.js-sandbox-toggle');
    const ownKeyToggle = content.querySelector('.js-own-key-toggle');
    const toggle = content.querySelector('.js-api-key-toggle');
    input?.addEventListener('change', () => {
      storage.setApiKey(input.value.trim());
    });
    sandboxToggle?.addEventListener('change', () => {
      storage.setMode(sandboxToggle.checked);
      render();
    });
    ownKeyToggle?.addEventListener('change', () => {
      storage.setUseOwnKey(ownKeyToggle.checked);
      render();
    });
    wireToggle(input, toggle);
  }

  function renderIbkrConfig() {
    const brokerMode = isBrokerMode();
    const apiKey = storage.getIBKRApiKey();
    const configured = !!apiKey;
    content.innerHTML =
      apiKeyFieldHtml(
        'IBKR Token',
        brokerMode ? 'Managed by backend broker' : 'Enter IBKR access token',
        brokerMode ? '' : apiKey,
        { disabled: brokerMode }
      ) + `
      <div class="api-label" style="margin-top:6px;">Mode: ${brokerMode ? 'Broker (Server Proxy)' : 'Client (Direct)'}</div>
      <div class="api-label" style="margin-top:6px;">Connection: ${configured ? 'Configured' : 'Not configured'}</div>
    `;
    const input = content.querySelector('.js-api-key');
    const toggle = content.querySelector('.js-api-key-toggle');
    input?.addEventListener('change', () => {
      storage.setIBKRApiKey(input.value.trim());
      render();
    });
    wireToggle(input, toggle);
  }

  function render() {
    const brokerId = getActiveBrokerId();
    const selectEl = brokerSelector.el.querySelector('#broker-selector-input');
    if (selectEl) selectEl.value = brokerId;
    if (brokerId === 'ibkr') renderIbkrConfig();
    else renderTradierConfig();
  }

  const unsubscribe = storage.onChange(() => {
    if (getBroker().brokerName === getActiveBrokerName()) {
      render();
    }
  });
  render();

  return {
    el,
    destroy() {
      unsubscribe();
      brokerSelector.destroy();
    }
  };
}
